// features/community/LikeCommunity.tsx

import axios from '@/lib/axios';
import React from 'react';
import { toast } from 'react-toastify';
import { IoHeartOutline } from 'react-icons/io5';
import { IoHeartSharp } from 'react-icons/io5';

// Define more specific types for the props
interface LikeCommunityProps {
  // `post` is now the single post object that is being liked. It's always required.
  post: {
    id: number | string;
    is_like: boolean;
    like_count: number;
    // ... other post properties
  };
  // `posts` is the full SWR data object from the list page. It is optional.
  posts?: any;
  // `mutate` is the SWR mutate function. It's always required.
  mutate: (data?: any, shouldRevalidate?: boolean) => Promise<any>;
}

function LikeCommunity({ post, posts, mutate }: LikeCommunityProps) {
  const handleLike = async () => {
    // This part is the same for both cases
    const newLikeStatus = !post.is_like;
    const newLikeCount = newLikeStatus
      ? post.like_count + 1
      : post.like_count - 1;

    let optimisticData;
    let originalData;

    // =======================================================
    //  THE DYNAMIC LOGIC IS HERE
    // =======================================================

    if (posts) {
      // CASE 1: We are on the LIST page (the `posts` prop was passed)
      originalData = posts; // The original data is the full list object
      const optimisticPosts = posts.data.map((p: any) =>
        p.id === post.id
          ? { ...p, is_like: newLikeStatus, like_count: newLikeCount }
          : p
      );
      optimisticData = { ...posts, data: optimisticPosts };
    } else {
      // CASE 2: We are on the SINGLE page (only `post` and `mutate` were passed)
      originalData = { data: [post] }; // The original data is just the single post
      // Optimistic data is the single post object with updated values
      const optimisticPost = {
        ...post,
        is_like: newLikeStatus,
        like_count: newLikeCount,
      };
      optimisticData = { data: [optimisticPost] }; // Wrap it to match the expected API structure
    }

    try {
      // This logic now works for both cases because `optimisticData` is correctly structured
      mutate(optimisticData, false);

      await axios.post(`/api/v2/community-like-update`, {
        post_id: post.id,
      });
    } catch (error: any) {
      toast.error(error.message || 'Failed to update like status');
      // Rollback using the correct original data structure
      mutate(originalData, false);
    }
  };

  // The button's display logic is based on the `post` prop, which is always present
  return (
    <button
      onClick={handleLike}
      className={`trk-btn trk-btn--md ${
        post.is_like ? 'trk-btn--outline' : 'trk-btn--secondary'
      }`}
    >
      {post.is_like ? (
        <IoHeartOutline className="fw-bold" />
      ) : (
        <IoHeartSharp className="fw-bold" />
      )}
      Like ({post?.like_count})
    </button>
  );
}

export default LikeCommunity;
